import { Children, cloneElement, isValidElement, useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useOnClickOutside, useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import { ariaProps } from '@repletoryreach/sdk-utils/a11y';
import { Portal } from '../../core/Portal/Portal.js';
import { computePosition, type Placement } from '../floating-positioner.js';
import './Menu.css';

export interface MenuProps {
  opened?: boolean;
  defaultOpened?: boolean;
  onChange?: (o: boolean) => void;
  position?: Placement;
  offset?: number;
  shadow?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  closeOnItemClick?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  children?: ReactNode;
  /** Optional fixed width for the menu. */
  width?: number | string;
}

interface MenuItemProps {
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  disabled?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | string;
  onClick?: (e: React.MouseEvent) => void;
  children?: ReactNode;
  closeMenuOnClick?: boolean;
  href?: string;
  as?: 'button' | 'a' | React.ElementType;
}

interface MenuLabelProps { children?: ReactNode }
interface MenuDividerProps { className?: string }

function Target({ children }: { children: ReactNode }) { return <>{children}</>; }
function Dropdown({ children }: { children: ReactNode }) { return <>{children}</>; }
function Label({ children }: MenuLabelProps) { return <div className="rr-menu__label">{children}</div>; }
function MenuDivider({ className }: MenuDividerProps) { return <div className={['rr-menu__divider', className].filter(Boolean).join(' ')} role="separator" />; }

function Item({ leftSection, rightSection, disabled, color, onClick, children, closeMenuOnClick: _ignore, href, as }: MenuItemProps): ReactNode {
  const Tag = (as ?? (href ? 'a' : 'button')) as React.ElementType;
  return (
    <Tag
      role="menuitem"
      href={href}
      type={Tag === 'button' ? 'button' : undefined}
      tabIndex={-1}
      disabled={disabled}
      data-disabled={disabled || undefined}
      data-rr-menu-color={color}
      className="rr-menu__item"
      onClick={onClick}
    >
      {leftSection ? <span className="rr-menu__item-section rr-menu__item-section--left">{leftSection}</span> : null}
      <span className="rr-menu__item-body">{children}</span>
      {rightSection ? <span className="rr-menu__item-section rr-menu__item-section--right">{rightSection}</span> : null}
    </Tag>
  );
}

Target.displayName = 'MenuTarget';
Dropdown.displayName = 'MenuDropdown';
Item.displayName = 'MenuItem';
Label.displayName = 'MenuLabel';
MenuDivider.displayName = 'MenuDivider';

interface MenuComponent extends React.FC<MenuProps> {
  Target: typeof Target;
  Dropdown: typeof Dropdown;
  Item: typeof Item;
  Label: typeof Label;
  Divider: typeof MenuDivider;
}

function MenuRoot({
  opened, defaultOpened, onChange,
  position = 'bottom-start', offset = 6,
  shadow = 'md',
  closeOnItemClick = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  children,
  width
}: MenuProps): ReactNode {
  const [isOpen, setOpen] = useUncontrolled<boolean>({
    value: opened, defaultValue: defaultOpened, finalValue: false, onChange
  });
  const anchorRef = useRef<HTMLElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  /*
   * Held in state, not only in a ref: `Portal` renders null until its own
   * effect has run, so the node does not exist on the commit where this opens.
   * Reading a still-null ref here made the effect bail and never run again,
   * leaving the panel parked at its pre-measurement -9999px.
   */
  const [dropdownEl, setDropdownEl] = useState<HTMLDivElement | null>(null);
  const setDropdownNode = (node: HTMLDivElement | null) => {
    dropdownRef.current = node;
    setDropdownEl(node);
  };
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen || !anchorRef.current || !dropdownEl) return;
    const a = anchorRef.current.getBoundingClientRect();
    const d = dropdownEl.getBoundingClientRect();
    setCoords(computePosition(a, { width: d.width, height: d.height }, { placement: position, offset }));
  }, [isOpen, position, offset, dropdownEl]);

  useOnClickOutside(dropdownRef, () => { if (isOpen && closeOnClickOutside) setOpen(false); });

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEscape, setOpen]);

  // Arrow-key roving within the dropdown.
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const items = Array.from(dropdownRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]:not([data-disabled])'));
    let active = 0;
    items[0]?.focus();
    const handler = (e: KeyboardEvent) => {
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); active = (active + 1) % items.length; items[active].focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); active = (active - 1 + items.length) % items.length; items[active].focus(); }
      else if (e.key === 'Home') { e.preventDefault(); active = 0; items[0].focus(); }
      else if (e.key === 'End') { e.preventDefault(); active = items.length - 1; items[active].focus(); }
    };
    const node = dropdownRef.current;
    node.addEventListener('keydown', handler);
    return () => node.removeEventListener('keydown', handler);
  }, [isOpen]);

  const arr = Children.toArray(children).filter(isValidElement) as ReactElement[];
  const target = arr.find((c) => (c.type as { displayName?: string }).displayName === 'MenuTarget');
  const drop = arr.find((c) => (c.type as { displayName?: string }).displayName === 'MenuDropdown');
  const t = target ? (target.props as { children?: ReactNode }).children : null;
  const d = drop ? (drop.props as { children?: ReactNode }).children : null;

  const enhanced = isValidElement(t)
    ? cloneElement(t as ReactElement<{ ref?: React.Ref<HTMLElement>; 'aria-haspopup'?: string; 'aria-expanded'?: boolean; 'aria-controls'?: string; onClick?: (e: React.MouseEvent) => void }>, {
        ref: (node: HTMLElement | null) => { anchorRef.current = node; },
        'aria-haspopup': 'menu',
        'aria-expanded': isOpen,
        'aria-controls': menuId,
        onClick: (e: React.MouseEvent) => {
          (t as ReactElement<{ onClick?: (e: React.MouseEvent) => void }>).props.onClick?.(e);
          setOpen(!isOpen);
        }
      })
    : t;

  return (
    <>
      {enhanced}
      {isOpen ? (
        <Portal>
          <div
            ref={setDropdownNode}
            id={menuId}
            {...ariaProps.menu()}
            className={`rr-menu rr-popover--shadow-${shadow}`}
            style={{
              position: 'fixed',
              top: coords ? `${coords.y}px` : '-9999px',
              left: coords ? `${coords.x}px` : '-9999px',
              width: typeof width === 'number' ? `${width}px` : width,
              zIndex: 'var(--rr-z-popover)' as unknown as number
            }}
            onClick={(e: React.MouseEvent) => {
              if (!closeOnItemClick) return;
              const t = e.target as HTMLElement;
              if (t.closest('[role="menuitem"]')) setOpen(false);
            }}
          >
            {d}
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export const Menu = MenuRoot as MenuComponent;
Menu.Target = Target;
Menu.Dropdown = Dropdown;
Menu.Item = Item;
Menu.Label = Label;
Menu.Divider = MenuDivider;
